import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  CaretLeft, MapPin, Star, ShieldCheck, Storefront, Package, Buildings, User,
} from "@phosphor-icons/react";
import api from "../lib/api";
import { fileUrl } from "../lib/api";
import { AchievementIcon } from "../lib/achievementIcons";
import ListingCard from "../components/ListingCard";

function RatingPill({ label, data, testid }) {
  const has = data && data.count > 0;
  return (
    <div className="flex-1 rounded-2xl bg-white border border-slate-200 p-4 text-center" data-testid={testid}>
      <div className="text-[11px] uppercase tracking-wide text-slate-400 font-bold">{label}</div>
      {has ? (
        <div className="mt-1 flex items-center justify-center gap-1">
          <Star size={20} weight="fill" color="#F59E0B" />
          <span className="text-2xl font-black text-slate-800">{data.avg.toFixed(1)}</span>
          <span className="text-sm text-slate-400 font-semibold">({data.count})</span>
        </div>
      ) : (
        <div className="mt-2 text-sm text-slate-400 font-semibold">Sem avaliações</div>
      )}
    </div>
  );
}

export default function PublicProfile() {
  const { userId } = useParams();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [listings, setListings] = useState([]); // V17: vitrine do locador

  useEffect(() => {
    setLoading(true);
    setError(false);
    api.get(`/users/${userId}/public`)
      .then((r) => setData(r.data))
      .catch(() => setError(true))
      .finally(() => setLoading(false));
    api.get(`/users/${userId}/listings`)
      .then((r) => setListings(r.data || []))
      .catch(() => setListings([]));
  }, [userId]);

  const memberYear = (() => {
    try { return data?.member_since ? new Date(data.member_since).getFullYear() : null; }
    catch { return null; }
  })();

  return (
    <div className="max-w-2xl mx-auto px-4 py-5" data-testid="public-profile-page">
      <button onClick={() => nav(-1)} className="inline-flex items-center gap-1 text-slate-500 text-sm font-bold mb-4" data-testid="public-profile-back">
        <CaretLeft size={16} weight="bold" /> Voltar
      </button>

      {loading && (
        <div className="text-center py-16 text-slate-400" data-testid="public-profile-loading">Carregando perfil…</div>
      )}

      {error && !loading && (
        <div className="text-center py-16 text-slate-500" data-testid="public-profile-error">
          Não foi possível carregar este perfil.
        </div>
      )}

      {data && !loading && (
        <div className="space-y-5">
          {/* Cabeçalho */}
          <div className="flex items-center gap-4">
            <div className="w-20 h-20 rounded-full bg-slate-100 grid place-items-center overflow-hidden ring-2 ring-[#004E98]/10 flex-shrink-0">
              {data.avatar ? (
                <img src={fileUrl(data.avatar)} alt={data.display_name} className="w-full h-full object-cover object-center" data-testid="public-profile-avatar" />
              ) : (
                data.is_pj ? <Buildings size={36} weight="fill" className="text-slate-400" />
                  : <User size={36} weight="fill" className="text-slate-400" />
              )}
            </div>
            <div className="min-w-0">
              <h1 className="font-heading text-2xl font-black truncate flex items-center gap-2" data-testid="public-profile-name">
                {data.display_name}
                {data.kyc_verified && (
                  <ShieldCheck size={20} weight="fill" className="text-emerald-500 flex-shrink-0" data-testid="public-profile-verified" />
                )}
              </h1>
              <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-slate-500 mt-0.5">
                {(data.city || data.uf) && (
                  <span className="flex items-center gap-1" data-testid="public-profile-location">
                    <MapPin size={14} weight="bold" /> {[data.city, data.uf].filter(Boolean).join(" - ")}
                  </span>
                )}
                {memberYear && <span data-testid="public-profile-member">Membro desde {memberYear}</span>}
              </div>
            </div>
          </div>

          {/* Notas */}
          <div className="flex gap-3">
            <RatingPill label="Como locador" data={data.owner_rating} testid="public-owner-rating" />
            <RatingPill label="Como locatário" data={data.renter_rating} testid="public-renter-rating" />
          </div>

          {/* Estatísticas */}
          <div className="flex gap-3">
            <div className="flex-1 rounded-2xl bg-[#004E98] text-white p-4 text-center" data-testid="public-active-listings">
              <Storefront size={22} weight="fill" className="mx-auto opacity-90" />
              <div className="text-2xl font-black mt-1">{data.active_listings}</div>
              <div className="text-[11px] uppercase tracking-wide opacity-80 font-bold">Anúncios ativos</div>
            </div>
            <div className="flex-1 rounded-2xl bg-[#FF6B35] text-white p-4 text-center" data-testid="public-completed-rentals">
              <Package size={22} weight="fill" className="mx-auto opacity-90" />
              <div className="text-2xl font-black mt-1">{data.completed_rentals}</div>
              <div className="text-[11px] uppercase tracking-wide opacity-80 font-bold">Locações concluídas</div>
            </div>
          </div>

          {/* V17: Vitrine — inventário do locador */}
          {listings.length > 0 && (
            <div data-testid="public-storefront">
              <div className="flex items-center justify-between mb-3">
                <h2 className="font-heading font-black text-lg flex items-center gap-2">
                  <Storefront size={20} weight="fill" className="text-[#FF6B35]" /> Vitrine do locador
                </h2>
                <span className="text-sm text-slate-400 font-bold" data-testid="public-storefront-count">{listings.length} item(ns)</span>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3" data-testid="public-storefront-grid">
                {listings.map((l) => (
                  <ListingCard key={l.id} item={l} testId={`storefront-item-${l.id}`} />
                ))}
              </div>
            </div>
          )}

          {/* Conquistas / badges */}
          <div className="rounded-2xl bg-slate-900 p-5" data-testid="public-achievements">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-white font-heading font-black text-lg">Conquistas</h2>
              <span className="text-amber-400 text-sm font-bold" data-testid="public-achievements-count">{data.achievements_count}</span>
            </div>
            {data.achievements_count > 0 ? (
              <div className="space-y-3" data-testid="public-achievements-list">
                {Object.values(
                  data.achievements.reduce((acc, a) => {
                    (acc[a.challenge_key] ||= { key: a.challenge_key, title: a.challenge_title, items: [] }).items.push(a);
                    return acc;
                  }, {})
                ).map((g) => {
                  const sorted = [...g.items].sort((x, y) => x.count - y.count);
                  const current = sorted[sorted.length - 1];
                  return (
                    <div key={g.key} className="rounded-2xl bg-white/[0.06] border border-white/10 p-3" data-testid={`public-badge-${g.key}`}>
                      <div className="flex items-center gap-3">
                        <div className="w-14 h-14 rounded-2xl grid place-items-center flex-shrink-0" style={{ background: `${current.color}33`, border: `2px solid ${current.color}` }}>
                          <AchievementIcon icon={current.icon} size={28} style={{ color: "#fff" }} />
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="text-[10px] uppercase tracking-wide text-white/55 font-bold truncate">{g.title}</div>
                          <div className="text-sm font-black text-white leading-tight truncate">{current.label}</div>
                          <div className="text-[11px] text-amber-400 font-bold">Nível {current.level} de {current.total_levels || sorted.length}</div>
                        </div>
                      </div>
                      {sorted.length > 1 && (
                        <div className="mt-3 pt-3 border-t border-white/10 flex flex-wrap gap-2" data-testid={`public-evolution-${g.key}`}>
                          {sorted.map((a, i) => (
                            <div key={i} className="flex items-center gap-1.5 bg-white/[0.05] rounded-full pl-1.5 pr-2.5 py-1">
                              <div className="w-6 h-6 rounded-full grid place-items-center" style={{ background: `${a.color}33`, border: `1.5px solid ${a.color}` }}>
                                <AchievementIcon icon={a.icon} size={12} style={{ color: "#fff" }} />
                              </div>
                              <span className="text-[10px] font-bold text-white/90 leading-none">{a.label}</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            ) : (
              <p className="text-slate-400 text-sm">Ainda sem medalhas conquistadas.</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
